import { cn } from "@/lib/utils";

import { minutes, percent, rate, type Performance } from "./performance";

export const Card = ({
  title,
  hint,
  children,
  className,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section
    className={cn(
      "flex flex-col gap-3 rounded-3xl border bg-card p-5",
      className,
    )}
  >
    <div>
      <h2 className="text-base font-semibold">{title}</h2>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
    {children}
  </section>
);

/** Thin horizontal bar: the data-end is rounded, the track is the surface. */
export const Bar = ({
  value,
  max,
  title,
  strong = false,
}: {
  value: number;
  max: number;
  title?: string;
  strong?: boolean;
}) => (
  <div className="h-2.5 w-full rounded-full bg-muted" title={title}>
    <div
      className={cn(
        "h-full rounded-full",
        strong ? "bg-foreground" : "bg-sky-600 dark:bg-sky-400",
      )}
      style={{
        width: `${max > 0 ? Math.max((value / max) * 100, value ? 2 : 0) : 0}%`,
      }}
    />
  </div>
);

/** Leads of the period, step by step, with the % that passes each step. */
export const FunnelCard = ({ perf }: { perf: Performance }) => {
  const s = perf.summary;
  const steps = [
    { label: "Leads", value: s.leads, hint: "Entraron en el periodo" },
    { label: "Llamados", value: s.called, hint: "Al menos una llamada" },
    {
      label: "Hablados",
      value: s.talked,
      hint: "Cogieron o contestaron por WhatsApp",
    },
    { label: "Cierre de compra", value: s.sure, hint: "Dijeron que sí" },
    { label: "Venta", value: s.sold, hint: "Tienen pedido" },
  ];
  return (
    <Card
      title="Embudo de los leads del periodo"
      hint="Lo que ha pasado hasta hoy con los leads que entraron en estas fechas"
    >
      <div className="flex flex-col gap-3">
        {steps.map((step, index) => (
          <div
            key={step.label}
            className="grid grid-cols-[8rem_1fr_6.5rem] items-center gap-3"
          >
            <div className="leading-tight">
              <p className="text-sm font-medium">{step.label}</p>
              <p className="text-[11px] text-muted-foreground">{step.hint}</p>
            </div>
            <Bar
              value={step.value}
              max={s.leads}
              strong={index === steps.length - 1}
              title={`${step.value} de ${s.leads} leads`}
            />
            <p className="text-right text-sm tabular-nums">
              <b>{step.value}</b>{" "}
              <span className="text-muted-foreground">
                {index ? percent(rate(step.value, s.leads)) : ""}
              </span>
            </p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 border-t pt-3 text-sm text-muted-foreground">
        <span>
          Sin hablar nunca: <b className="text-foreground">{s.unreached}</b>
        </span>
        <span>
          En curso: <b className="text-foreground">{s.open}</b>
        </span>
        <span>
          No compran: <b className="text-foreground">{s.lost}</b>
        </span>
        <span>
          Días hasta la venta (mediana):{" "}
          <b className="text-foreground">
            {s.days_to_sale_median == null
              ? "—"
              : s.days_to_sale_median.toLocaleString("es-ES")}
          </b>
        </span>
        <span>
          Intentos hasta hablar:{" "}
          <b className="text-foreground">
            {s.attempts_to_talk_avg == null
              ? "—"
              : (Number(s.attempts_to_talk_avg) + 1).toLocaleString("es-ES")}
          </b>
        </span>
      </div>
    </Card>
  );
};

/** How fast we call, and what speed is worth in sales. */
export const SpeedCard = ({ perf }: { perf: Performance }) => {
  const sp = perf.speed;
  const fastClose = rate(sp.fast_sold, sp.fast_leads);
  const slowClose = rate(sp.slow_sold, sp.slow_leads);
  const rows = [
    { label: "En menos de 5 min", value: sp.within_5 },
    { label: "En menos de 15 min", value: sp.within_15 },
    { label: "En menos de 1 hora", value: sp.within_60 },
    { label: "El mismo día", value: sp.same_day },
  ];
  return (
    <Card
      title="Velocidad de llamada"
      hint="Leads que entraron entre las 9 y las 21 h: cuánto tardamos en llamarles"
    >
      <p className="text-sm">
        Mediana hasta la primera llamada:{" "}
        <b className="text-lg tabular-nums">{minutes(sp.median_minutes)}</b>
      </p>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[9rem_1fr_4.5rem] items-center gap-3"
          >
            <span className="text-sm">{row.label}</span>
            <Bar
              value={row.value}
              max={sp.daytime_leads}
              title={`${row.value} de ${sp.daytime_leads}`}
            />
            <span className="text-right text-sm tabular-nums">
              {percent(rate(row.value, sp.daytime_leads))}
            </span>
          </div>
        ))}
      </div>
      {fastClose != null && slowClose != null ? (
        <p className="rounded-2xl bg-muted p-3 text-sm">
          Llamados en menos de 1 h cierran el <b>{percent(fastClose)}</b>; los
          que se llaman más tarde o nunca, el <b>{percent(slowClose)}</b>.
        </p>
      ) : null}
    </Card>
  );
};

/** Calls per hour of the day and how many were answered. */
export const HoursCard = ({ perf }: { perf: Performance }) => {
  const hours = perf.hours.filter((h) => h.hour >= 8 && h.hour <= 22);
  const best = [...hours]
    .filter((h) => h.calls >= 10)
    .sort((a, b) => b.answered / b.calls - a.answered / a.calls)[0];
  return (
    <Card
      title="A qué hora cogen el teléfono"
      hint="Llamadas hechas en el periodo por hora y % que se cogieron"
    >
      {hours.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin llamadas en el periodo.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {hours.map((h) => (
            <div
              key={h.hour}
              className="grid grid-cols-[3.5rem_1fr_7.5rem] items-center gap-3"
            >
              <span className="text-sm tabular-nums">{h.hour}:00</span>
              <Bar
                value={h.answered}
                max={h.calls}
                title={`${h.answered} cogidas de ${h.calls} llamadas`}
              />
              <span className="text-right text-xs tabular-nums text-muted-foreground">
                <b className="text-foreground">
                  {percent(rate(h.answered, h.calls))}
                </b>{" "}
                de {h.calls}
              </span>
            </div>
          ))}
        </div>
      )}
      {best ? (
        <p className="rounded-2xl bg-muted p-3 text-sm">
          La mejor hora es a las <b>{best.hour}:00</b>: se cogen el{" "}
          <b>{percent(rate(best.answered, best.calls))}</b> de las llamadas.
        </p>
      ) : null}
    </Card>
  );
};
